import React, { ChangeEvent, useState } from 'react';
import { Button } from 'antd';
import { useMutation } from '@apollo/client';
// Helpers
import { parseForInnerHTMLWithBreaks } from 'helpers/textEditor';
import { getSeparatedDeliverableItems } from 'helpers/deliverables';
// API
import {
  APPROVE_DELIVERABLES,
  REJECT_DELIVERABLES,
} from 'api/campaignV2/mutations';
// Types
import {
  ApproveDeliverables,
  ApproveDeliverablesVariables,
} from 'api/campaignV2/types/ApproveDeliverables';
import {
  RejectDeliverables,
  RejectDeliverablesVariables,
} from 'api/campaignV2/types/RejectDeliverables';
import {
  GetCampaignOffers_getCampaignOffers_entities,
  GetCampaignOffers_getCampaignOffers_entities_deliverables,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2,
} from 'api/campaignV2/types/GetCampaignOffers';
import {
  DeliverableReviewAction,
  DeliverableReviewItemInput,
  CampaignType,
} from 'api/graphql-global-types';
// UiShared
import TextArea from 'uiShared/Textarea/Textarea';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import DeliverablesReview from './components/DeliverablesReview/DeliverablesReview';
// Hooks
import { useReviewDeliverables } from 'hooks/useReviewDeliverables';
import TimelineDeliverablesReviewForm from './components/TimelineDeliverablesReviewForm/TimelineDeliverablesReviewForm';
// Styles
import styles from './CampaignDeliverablesSubmissionReview.module.scss';

type CampaignDeliverablesSubmissionReviewProps = {
  deliverable:
    | GetCampaignOffers_getCampaignOffers_entities_deliverables
    | GetCampaignOffers_getCampaignOffers_entities_deliverablesV2;
  offer: GetCampaignOffers_getCampaignOffers_entities;
  offerId: string;
  ownerId: string;
  isV2Campaign: boolean;
  isV3Campaign?: boolean;
  onFinish: () => void;
};

const CampaignDeliverablesSubmissionReview = ({
  deliverable,
  offer,
  offerId,
  ownerId,
  isV2Campaign,
  isV3Campaign = false,
  onFinish,
}: CampaignDeliverablesSubmissionReviewProps): JSX.Element => {
  const [agencyFeedback, setAgencyFeedback] = useState<string>('');
  const [showFeedback, setShowFeedback] = useState<boolean>(false);
  const [isV3Loading, setIsV3Loading] = useState<boolean>(false);

  // V3 campaigns use the new review system
  const { reviewDeliverables } = useReviewDeliverables();

  // V1/V2 campaigns use the old system
  const [approveDeliverable, { loading: approveLoading }] = useMutation<
    ApproveDeliverables,
    ApproveDeliverablesVariables
  >(APPROVE_DELIVERABLES);

  const [rejectDeliverable, { loading: rejectLoading }] = useMutation<
    RejectDeliverables,
    RejectDeliverablesVariables
  >(REJECT_DELIVERABLES);

  const handleToggleFeedback = () => {
    setShowFeedback((previousState) => !previousState);
  };

  const handleFeedbackChange = (newValue: ChangeEvent<HTMLTextAreaElement>) => {
    const feedback = newValue.target.value;
    if (feedback !== '') {
      setAgencyFeedback(feedback);
    } else {
      setAgencyFeedback('');
    }
  };

  // V3 campaigns: Create review items for the new API
  const createV3ReviewItems = (
    action: DeliverableReviewAction
  ): DeliverableReviewItemInput[] => {
    if (!isV3Campaign || !('task' in deliverable)) {
      return [];
    }

    const { files, copies } = getSeparatedDeliverableItems(deliverable);
    const reviewItems: DeliverableReviewItemInput[] = [];

    // Add file items
    files.forEach((fileItem) => {
      reviewItems.push({
        deliverableItemId: fileItem.id,
        action,
        feedback:
          action === DeliverableReviewAction.REJECT ? agencyFeedback : null,
      });
    });

    // Add copy items
    copies.forEach((copyItem) => {
      reviewItems.push({
        deliverableItemId: copyItem.id,
        action,
        feedback:
          action === DeliverableReviewAction.REJECT ? agencyFeedback : null,
      });
    });

    return reviewItems;
  };

  const handleApprove = async () => {
    setIsV3Loading(true);
    try {
      if (isV3Campaign) {
        // V3 campaigns: Use new review API
        const reviewItems = createV3ReviewItems(
          DeliverableReviewAction.APPROVE
        );

        await reviewDeliverables({
          variables: {
            input: {
              offerId,
              deliverableItems: reviewItems,
            },
          },
        });
      } else {
        // V1/V2 campaigns: Use old approve API
        await approveDeliverable({
          variables: {
            input: {
              ownerId,
              offerId,
              deliverableId: deliverable.id,
            },
          },
        });
      }

      successNotification('Approved successfully!');
      onFinish();
    } catch (error: any) {
      console.error('Error approving deliverables:', error);
      errorNotification('Something went wrong, please try again later');
    } finally {
      setIsV3Loading(false);
    }
  };

  const handleReject = async () => {
    setIsV3Loading(true);
    try {
      if (isV3Campaign) {
        // V3 campaigns: Use new review API
        const reviewItems = createV3ReviewItems(DeliverableReviewAction.REJECT);

        await reviewDeliverables({
          variables: {
            input: {
              offerId,
              deliverableItems: reviewItems,
            },
          },
        });
      } else {
        // V1/V2 campaigns: Use old reject API
        await rejectDeliverable({
          variables: {
            input: {
              ownerId,
              offerId,
              deliverableId: deliverable.id,
              agencyFeedback,
            },
          },
        });
      }

      successNotification('Rejection submitted.');
      onFinish();
    } catch (error: any) {
      console.error('Error rejecting deliverables:', error);
      errorNotification('Something went wrong, please try again later');
    } finally {
      setIsV3Loading(false);
    }
  };

  const isLoading = isV3Campaign
    ? isV3Loading
    : approveLoading || rejectLoading;

  const isSocialShareCampaign =
    offer.campaign.type === CampaignType.SocialShare;

  return (
    <div className={styles.root}>
      {isV3Campaign ? (
        <TimelineDeliverablesReviewForm
          deliverable={
            deliverable as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
          }
          offer={offer}
          onClose={onFinish}
          refetchOffers={onFinish}
        />
      ) : (
        <>
          {showFeedback ? (
            <div>
              <p className={styles.label}>Feedback</p>
              <TextArea
                placeholder="Provide detailed, specific feedback to ensure talent revisions meet approval requirements."
                name="feedback"
                onChange={handleFeedbackChange}
                className={styles.textarea}
                rows={3}
              />
              <div className={styles.buttonWrapper}>
                <Button
                  className={styles.button}
                  loading={isLoading}
                  onClick={handleToggleFeedback}
                  type="primary"
                  ghost
                >
                  Back to Deliverables
                </Button>

                <Button
                  className={styles.button}
                  loading={isLoading}
                  onClick={handleReject}
                  type="primary"
                >
                  Submit Feedback
                </Button>
              </div>
            </div>
          ) : (
            <div className={styles.reviewWrapper}>
              {/* Show post copy for V1 campaigns only, but exclude social share campaigns */}
              {!isV2Campaign &&
                !isV3Campaign &&
                !isSocialShareCampaign &&
                'postCopy' in deliverable && (
                  <div className={styles.inputWrapper}>
                    <p className={styles.label}>Post Copy</p>
                    <p
                      className={styles.value}
                      dangerouslySetInnerHTML={{
                        __html: parseForInnerHTMLWithBreaks(
                          deliverable.postCopy
                        ),
                      }}
                    />
                  </div>
                )}

              <div className={styles.previewsContainer}>
                <DeliverablesReview deliverable={deliverable} />
              </div>

              <div className={styles.buttonWrapper}>
                <Button
                  className={styles.button}
                  loading={isLoading}
                  onClick={handleApprove}
                  type="primary"
                >
                  Approve Deliverables
                </Button>

                <Button
                  className={styles.button}
                  loading={isLoading}
                  onClick={handleToggleFeedback}
                  type="primary"
                  danger
                >
                  Reject Deliverables
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CampaignDeliverablesSubmissionReview;
